import React, { useState, useEffect } from 'react';
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe,
  FileText,
  CreditCard,
  Calendar,
  Upload,
  Save,
  AlertCircle,
  CheckCircle,
  Loader2,
  Trash2,
  Image,
  Briefcase,
  Users,
  Shield,
  Euro,
  Hash,
  Clock,
} from 'lucide-react';
import { useAuth, ROLES, ACCOUNT_TYPES } from '../context/AuthContext';
import { useCompanyTheme } from '../context/CompanyThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { useActivityLog, LOG_TYPES, LOG_CATEGORIES } from '../context/ActivityLogContext';

const CompanySettings = () => {
  const { t } = useTranslation();
  const { user, hasRole } = useAuth();
  const { theme, updateCompanyInfo } = useCompanyTheme();
  const { addLog } = useActivityLog();
  const [activeTab, setActiveTab] = useState('general');
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });


  const defaultCompanyInfo = {
    name: user?.company || '',
    legalName: '',
    description: '',
    industry: 'finance',
    size: '1-10',
    foundedYear: '',
    website: '',
    address: '',
    addressLine2: '',
    city: '',
    postalCode: '',
    country: 'France',
    phone: '',
    email: '',
    siret: '',
    siren: '',
    tvaNumber: '',
    rcs: '',
    capitalSocial: '',
    legalForm: 'sas',
    apeCode: '',
    iban: '',
    bic: '',
    bankName: '',
    billingEmail: '',
    billingAddress: '',
    paymentTerms: '30',
    currency: 'EUR',
    logo: null,
    primaryColor: '#3b82f6',
    secondaryColor: '#1e293b',
  };

  const [companyInfo, setCompanyInfo] = useState(defaultCompanyInfo);
  const [logo, setLogo] = useState(null);


  useEffect(() => {
    const loadCompanySettings = async () => {
      if (!user?.id) return;

      setDataLoading(true);
      try {
        const result = await window.electronAPI?.user?.getSettings(user.id);
        if (result?.success && result.data?.companySettings) {
          const savedSettings = result.data.companySettings;
          setCompanyInfo(prev => ({
            ...prev,
            ...savedSettings,
          }));
          setLogo(savedSettings.logo || null);
        } else {

          setCompanyInfo(prev => ({
            ...prev,
            name: theme.companyName || user?.company || '',
            legalName: theme.legalName || '',
            website: theme.website || '',
            address: theme.address || '',
            city: theme.city || '',
            postalCode: theme.postalCode || '',
            country: theme.country || 'France',
            phone: theme.phone || '',
            email: theme.email || '',
            siret: theme.siret || '',
            tvaNumber: theme.tvaNumber || '',
            iban: theme.iban || '',
            bic: theme.bic || '',
            bankName: theme.bankName || '',
            billingEmail: theme.billingEmail || '',
            currency: theme.currency || 'EUR',
            logo: theme.logo || null,
            primaryColor: theme.primaryColor || '#3b82f6',
            secondaryColor: theme.secondaryColor || '#1e293b',
          }));
          setLogo(theme.logo || null);
        }
      } catch (error) {
        console.error('Error loading company settings:', error);
      }
      setDataLoading(false);
    };

    loadCompanySettings();
  }, [user?.id, theme]);

  const handleChange = (field, value) => {
    setCompanyInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setMessage({ type: 'error', text: t('companySettings.logoSizeError') });
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogo(e.target?.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);

    try {

      const currentSettingsResult = await window.electronAPI?.user?.getSettings(user.id);
      const currentSettings = currentSettingsResult?.data || {};


      const settingsToSave = {
        ...currentSettings,
        companySettings: {
          ...companyInfo,
          logo: logo,
        }
      };

      const result = await window.electronAPI?.user?.updateSettings(user.id, settingsToSave);

      if (result?.success) {

        updateCompanyInfo({
          ...companyInfo,
          logo: logo,
        });


        addLog({
          type: LOG_TYPES.ACTION,
          category: LOG_CATEGORIES.CONFIG,
          action: 'update_company_settings',
          message: `Paramètres entreprise mis à jour: ${companyInfo.name}`,
          details: {
            companyName: companyInfo.name,
            siret: companyInfo.siret,
            city: companyInfo.city,
            country: companyInfo.country
          }
        });

        setMessage({ type: 'success', text: t('companySettings.updateSuccess') });
      } else {
        setMessage({ type: 'error', text: result?.error || 'Erreur lors de la sauvegarde' });
      }
    } catch (error) {
      console.error('Error saving company settings:', error);
      setMessage({ type: 'error', text: error.message });
    }

    setIsLoading(false);
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const tabs = [
    { id: 'general', label: t('companySettings.generalInfo'), icon: Building2 },
    { id: 'contact', label: t('companySettings.contact'), icon: MapPin },
    { id: 'legal', label: t('companySettings.legalInfo'), icon: FileText },
    { id: 'billing', label: t('companySettings.billing'), icon: CreditCard },
    { id: 'branding', label: t('companySettings.branding'), icon: Image },
  ];

  const industries = [
    { value: 'finance', label: t('companySettings.industries.finance') },
    { value: 'insurance', label: t('companySettings.industries.insurance') },
    { value: 'consulting', label: t('companySettings.industries.consulting') },
    { value: 'technology', label: t('companySettings.industries.technology') },
    { value: 'healthcare', label: t('companySettings.industries.healthcare') },
    { value: 'manufacturing', label: t('companySettings.industries.manufacturing') },
    { value: 'retail', label: t('companySettings.industries.retail') },
    { value: 'real_estate', label: t('companySettings.industries.realEstate') },
    { value: 'energy', label: t('companySettings.industries.energy') },
    { value: 'other', label: t('companySettings.industries.other') },
  ];

  const companySizes = [
    { value: '1-10', label: t('companySettings.sizes.1-10') },
    { value: '11-50', label: t('companySettings.sizes.11-50') },
    { value: '51-200', label: t('companySettings.sizes.51-200') },
    { value: '201-500', label: t('companySettings.sizes.201-500') || '201-500 employés' },
    { value: '501-1000', label: t('companySettings.sizes.501-1000') || '501-1000 employés' },
    { value: '1001-5000', label: t('companySettings.sizes.1001-5000') || '1001-5000 employés' },
    { value: '5001-10000', label: t('companySettings.sizes.5001-10000') || '5001-10000 employés' },
    { value: '10000+', label: t('companySettings.sizes.10000+') || 'Plus de 10000 employés' },
  ];

  const legalForms = [
    { value: 'sa', label: t('companySettings.legalForms.sa') || 'Société Anonyme (SA)' },
    { value: 'sas', label: t('companySettings.legalForms.sas') || 'Société par Actions Simplifiée (SAS)' },
    { value: 'sarl', label: t('companySettings.legalForms.sarl') || 'SARL' },
    { value: 'eurl', label: t('companySettings.legalForms.eurl') || 'EURL' },
    { value: 'sca', label: t('companySettings.legalForms.sca') || 'Société en Commandite par Actions (SCA)' },
    { value: 'snc', label: t('companySettings.legalForms.snc') || 'Société en Nom Collectif (SNC)' },
    { value: 'ei', label: t('companySettings.legalForms.ei') || 'Entreprise Individuelle' },
    { value: 'other', label: t('companySettings.legalForms.other') || 'Autre' },
  ];

  const countries = [
    { value: 'France', label: 'France' },
    { value: 'Belgium', label: t('companySettings.countries.belgium') || 'Belgique' },
    { value: 'Switzerland', label: t('companySettings.countries.switzerland') || 'Suisse' },
    { value: 'Luxembourg', label: 'Luxembourg' },
    { value: 'Germany', label: t('companySettings.countries.germany') || 'Allemagne' },
    { value: 'Italy', label: t('companySettings.countries.italy') || 'Italie' },
    { value: 'Spain', label: t('companySettings.countries.spain') || 'Espagne' },
    { value: 'United Kingdom', label: t('companySettings.countries.uk') || 'Royaume-Uni' },
    { value: 'Netherlands', label: t('companySettings.countries.netherlands') || 'Pays-Bas' },
    { value: 'Other', label: t('companySettings.countries.other') || 'Autre' },
  ];

  if (user?.accountType !== ACCOUNT_TYPES.ENTERPRISE && !hasRole(ROLES.AGENT)) {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto text-center py-16">
          <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">{t('companySettings.title')}</h2>
          <p className="text-slate-500 mb-6">
            {t('companySettings.enterpriseOnly') || 'Cette section est réservée aux comptes entreprise. Veuillez mettre à niveau votre compte pour accéder à ces fonctionnalités.'}
          </p>
          <button className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors">
            {t('companySettings.upgradeToEnterprise') || 'Mettre à niveau vers Entreprise'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <Building2 className="w-7 h-7 text-blue-600" />
            {t('companySettings.title')}
          </h1>
          <p className="text-slate-500 mt-1">
            {t('companySettings.subtitle')}
          </p>
        </div>

        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="text-sm">{message.text}</p>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6">
          <div className="lg:w-64 flex-shrink-0">
            <nav className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                      : 'text-slate-600 hover:bg-slate-50 border-l-4 border-transparent'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="font-medium text-sm">{tab.label}</span>
                </button>
              ))}
            </nav>

            <div className="mt-6 bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">{t('companySettings.preview') || 'Aperçu'}</h3>
              <div className="flex items-center gap-3 mb-3">
                {logo ? (
                  <img src={logo} alt={t('companySettings.logo')} className="w-12 h-12 rounded-lg object-cover" />
                ) : (
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: companyInfo.primaryColor }}
                  >
                    {companyInfo.name?.substring(0, 2).toUpperCase()}
                  </div>
                )}
                <div>
                  <p className="font-medium text-slate-800">{companyInfo.name}</p>
                  <p className="text-xs text-slate-500">{companyInfo.city}, {companyInfo.country}</p>
                </div>
              </div>
              <div className="text-xs text-slate-500 space-y-1">
                <p>SIRET: {companyInfo.siret}</p>
                <p>TVA: {companyInfo.tvaNumber}</p>
              </div>
            </div>
          </div>

          <div className="flex-1">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">

              {activeTab === 'general' && (
                <div>
                  <h2 className="text-lg font-semibold text-slate-800 mb-6">
                    {t('companySettings.generalInfo') || 'Informations générales'}
                  </h2>

                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('companySettings.tradeName') || 'Nom commercial'} *
                        </label>
                        <div className="relative">
                          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <input
                            type="text"
                            value={companyInfo.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder={t('companySettings.companyName')}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('companySettings.legalName')}
                        </label>
                        <input
                          type="text"
                          value={companyInfo.legalName}
                          onChange={(e) => handleChange('legalName', e.target.value)}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder={t('companySettings.legalNamePlaceholder') || 'Raison sociale complète'}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('companySettings.companyDescription') || 'Description de l\'entreprise'}
                      </label>
                      <textarea
                        value={companyInfo.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        rows={3}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        placeholder={t('companySettings.descriptionPlaceholder') || 'Décrivez votre activité...'}
                      />
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('companySettings.industry') || 'Secteur d\'activité'}
                        </label>
                        <div className="relative">
                          <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <select
                            value={companyInfo.industry}
                            onChange={(e) => handleChange('industry', e.target.value)}
                            className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                          >
                            {industries.map((ind) => (
                              <option key={ind.value} value={ind.value}>{ind.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('companySettings.companySize') || 'Taille de l\'entreprise'}
                        </label>
                        <div className="relative">
                          <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <select
                            value={companyInfo.size}
                            onChange={(e) => handleChange('size', e.target.value)}
                            className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                          >
                            {companySizes.map((size) => (
                              <option key={size.value} value={size.value}>{size.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('companySettings.foundedYear') || 'Année de création'}
                        </label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <input
                            type="text"
                            value={companyInfo.foundedYear}
                            onChange={(e) => handleChange('foundedYear', e.target.value)}
                            className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="YYYY"
                            maxLength={4}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('companySettings.website') || 'Site web'}
                      </label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="url"
                          value={companyInfo.website}
                          onChange={(e) => handleChange('website', e.target.value)}
                          className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="https://www.example.com"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'contact' && (
                <div>
                  <h2 className="text-lg font-semibold text-slate-800 mb-6">
                    {t('companySettings.contact') || 'Coordonnées'}
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('common.address')} *
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                        <input
                          type="text"
                          value={companyInfo.address}
                          onChange={(e) => handleChange('address', e.target.value)}
                          className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder={t('companySettings.addressPlaceholder') || 'Numéro et rue'}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('companySettings.addressLine2') || 'Complément d\'adresse'}
                      </label>
                      <input
                        type="text"
                        value={companyInfo.addressLine2}
                        onChange={(e) => handleChange('addressLine2', e.target.value)}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder={t('companySettings.addressLine2Placeholder') || 'Bâtiment, étage, etc.'}
                      />
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('companySettings.postalCode') || 'Code postal'} *
                        </label>
                        <input
                          type="text"
                          value={companyInfo.postalCode}
                          onChange={(e) => handleChange('postalCode', e.target.value)}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="75000"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('companySettings.city') || 'Ville'} *
                        </label>
                        <input
                          type="text"
                          value={companyInfo.city}
                          onChange={(e) => handleChange('city', e.target.value)}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder={t('companySettings.cityPlaceholder') || 'Ville'}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('companySettings.country') || 'Pays'} *
                        </label>
                        <select
                          value={companyInfo.country}
                          onChange={(e) => handleChange('country', e.target.value)}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {countries.map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('common.phone')}
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <input
                            type="tel"
                            value={companyInfo.phone}
                            onChange={(e) => handleChange('phone', e.target.value)}
                            className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="+33 1 23 45 67 89"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('companySettings.contactEmail') || 'Email de contact'}
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <input
                            type="email"
                            value={companyInfo.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="contact@entreprise.com"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'legal' && (
                <div>
                  <h2 className="text-lg font-semibold text-slate-800 mb-6">
                    {t('companySettings.legalInfo') || 'Informations légales'}
                  </h2>

                  <div className="space-y-6">
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800">{t('companySettings.importantInfo') || 'Information importante'}</p>
                          <p className="text-sm text-amber-700">
                            {t('companySettings.legalInfoWarning') || 'Ces informations sont utilisées pour la génération des documents légaux et factures. Assurez-vous qu\'elles sont exactes et à jour.'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('companySettings.siretNumber') || 'Numéro SIRET'} *
                        </label>
                        <div className="relative">
                          <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <input
                            type="text"
                            value={companyInfo.siret}
                            onChange={(e) => handleChange('siret', e.target.value)}
                            className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="XXX XXX XXX XXXXX"
                            maxLength={17}
                          />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{t('companySettings.14digits') || '14 chiffres'}</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('companySettings.sirenNumber') || 'Numéro SIREN'}
                        </label>
                        <div className="relative">
                          <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <input
                            type="text"
                            value={companyInfo.siren}
                            onChange={(e) => handleChange('siren', e.target.value)}
                            className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="XXX XXX XXX"
                            maxLength={11}
                          />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{t('companySettings.9digits') || '9 chiffres'}</p>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('companySettings.vatNumber')}
                        </label>
                        <input
                          type="text"
                          value={companyInfo.tvaNumber}
                          onChange={(e) => handleChange('tvaNumber', e.target.value)}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="FRXX XXXXXXXXX"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('companySettings.rcs') || 'RCS'}
                        </label>
                        <input
                          type="text"
                          value={companyInfo.rcs}
                          onChange={(e) => handleChange('rcs', e.target.value)}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder={t('companySettings.rcsPlaceholder') || 'Ville B XXX XXX XXX'}
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('companySettings.legalForm') || 'Forme juridique'}
                        </label>
                        <select
                          value={companyInfo.legalForm}
                          onChange={(e) => handleChange('legalForm', e.target.value)}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {legalForms.map((form) => (
                            <option key={form.value} value={form.value}>{form.label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('companySettings.apeCode') || 'Code APE/NAF'}
                        </label>
                        <input
                          type="text"
                          value={companyInfo.apeCode}
                          onChange={(e) => handleChange('apeCode', e.target.value)}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="XXXXZ"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('companySettings.shareCapital') || 'Capital social'}
                      </label>
                      <div className="relative">
                        <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="text"
                          value={companyInfo.capitalSocial}
                          onChange={(e) => handleChange('capitalSocial', e.target.value)}
                          className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="10 000"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'billing' && (
                <div>
                  <h2 className="text-lg font-semibold text-slate-800 mb-6">
                    {t('companySettings.billingSettings') || 'Paramètres de facturation'}
                  </h2>

                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('companySettings.billingEmail') || 'Email de facturation'}
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <input
                            type="email"
                            value={companyInfo.billingEmail}
                            onChange={(e) => handleChange('billingEmail', e.target.value)}
                            className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="facturation@entreprise.com"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('companySettings.paymentTerms') || 'Délai de paiement (jours)'}
                        </label>
                        <div className="relative">
                          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <select
                            value={companyInfo.paymentTerms}
                            onChange={(e) => handleChange('paymentTerms', e.target.value)}
                            className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="0">{t('companySettings.immediatePayment') || 'Paiement immédiat'}</option>
                            <option value="15">{t('companySettings.15days') || '15 jours'}</option>
                            <option value="30">{t('companySettings.30days') || '30 jours'}</option>
                            <option value="45">{t('companySettings.45days') || '45 jours'}</option>
                            <option value="60">{t('companySettings.60days') || '60 jours'}</option>
                            <option value="90">{t('companySettings.90days') || '90 jours'}</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('companySettings.billingAddress') || 'Adresse de facturation'}
                      </label>
                      <textarea
                        value={companyInfo.billingAddress}
                        onChange={(e) => handleChange('billingAddress', e.target.value)}
                        rows={2}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        placeholder={t('companySettings.billingAddressPlaceholder') || 'Adresse complète de facturation'}
                      />
                    </div>

                    <div className="border-t border-slate-200 pt-6">
                      <h3 className="text-sm font-semibold text-slate-800 mb-4">
                        {t('companySettings.bankDetails') || 'Coordonnées bancaires'}
                      </h3>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            {t('companySettings.iban') || 'IBAN'}
                          </label>
                          <input
                            type="text"
                            value={companyInfo.iban}
                            onChange={(e) => handleChange('iban', e.target.value)}
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                            placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            {t('companySettings.bic') || 'BIC/SWIFT'}
                          </label>
                          <input
                            type="text"
                            value={companyInfo.bic}
                            onChange={(e) => handleChange('bic', e.target.value)}
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                            placeholder="BNPAFRPP"
                          />
                        </div>
                      </div>

                      <div className="mt-4">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('companySettings.bankName') || 'Nom de la banque'}
                        </label>
                        <input
                          type="text"
                          value={companyInfo.bankName}
                          onChange={(e) => handleChange('bankName', e.target.value)}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder={t('companySettings.bankNamePlaceholder') || 'Nom de votre banque'}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('companySettings.defaultCurrency')}
                      </label>
                      <select
                        value={companyInfo.currency}
                        onChange={(e) => handleChange('currency', e.target.value)}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="EUR">EUR - Euro (€)</option>
                        <option value="USD">USD - Dollar américain ($)</option>
                        <option value="GBP">GBP - Livre sterling (£)</option>
                        <option value="CHF">CHF - Franc suisse (CHF)</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'branding' && (
                <div>
                  <h2 className="text-lg font-semibold text-slate-800 mb-6">
                    {t('companySettings.branding')}
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('companySettings.companyLogo') || 'Logo de l\'entreprise'}
                      </label>
                      <div className="flex items-start gap-6">
                        <div className="w-32 h-32 border-2 border-dashed border-slate-300 rounded-xl flex items-center justify-center bg-slate-50 overflow-hidden">
                          {logo ? (
                            <img src={logo} alt={t('companySettings.logo')} className="w-full h-full object-contain" />
                          ) : (
                            <div className="text-center">
                              <Image className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                              <p className="text-xs text-slate-500">{t('companySettings.noLogo') || 'Pas de logo'}</p>
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg cursor-pointer transition-colors">
                            <Upload className="w-4 h-4" />
                            {t('companySettings.uploadLogo')}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleLogoUpload}
                              className="hidden"
                            />
                          </label>
                          {logo && (
                            <button
                              onClick={() => setLogo(null)}
                              className="ml-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                          <p className="text-xs text-slate-500 mt-2">
                            {t('companySettings.logoRequirements') || 'PNG, JPG ou SVG. Max 2 Mo. Recommandé: 512x512px'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-slate-200 pt-6">
                      <h3 className="text-sm font-semibold text-slate-800 mb-4">
                        {t('companySettings.brandColors') || 'Couleurs de marque'}
                      </h3>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            {t('companySettings.primaryColor')}
                          </label>
                          <div className="flex items-center gap-3">
                            <input
                              type="color"
                              value={companyInfo.primaryColor}
                              onChange={(e) => handleChange('primaryColor', e.target.value)}
                              className="w-12 h-12 rounded-lg cursor-pointer border border-slate-300"
                            />
                            <input
                              type="text"
                              value={companyInfo.primaryColor}
                              onChange={(e) => handleChange('primaryColor', e.target.value)}
                              className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                              placeholder="#000000"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            {t('companySettings.secondaryColor')}
                          </label>
                          <div className="flex items-center gap-3">
                            <input
                              type="color"
                              value={companyInfo.secondaryColor}
                              onChange={(e) => handleChange('secondaryColor', e.target.value)}
                              className="w-12 h-12 rounded-lg cursor-pointer border border-slate-300"
                            />
                            <input
                              type="text"
                              value={companyInfo.secondaryColor}
                              onChange={(e) => handleChange('secondaryColor', e.target.value)}
                              className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                              placeholder="#000000"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-slate-200 pt-6">
                      <h3 className="text-sm font-semibold text-slate-800 mb-4">
                        {t('companySettings.preview') || 'Aperçu'}
                      </h3>
                      <div className="p-6 bg-slate-100 rounded-xl">
                        <div className="bg-white rounded-lg shadow-sm p-6">
                          <div className="flex items-center gap-4 mb-4">
                            {logo ? (
                              <img src={logo} alt={t('companySettings.logo')} className="w-16 h-16 rounded-lg object-contain" />
                            ) : (
                              <div
                                className="w-16 h-16 rounded-lg flex items-center justify-center text-white text-xl font-bold"
                                style={{ backgroundColor: companyInfo.primaryColor }}
                              >
                                {companyInfo.name?.substring(0, 2).toUpperCase()}
                              </div>
                            )}
                            <div>
                              <h4 className="text-xl font-bold" style={{ color: companyInfo.secondaryColor }}>
                                {companyInfo.name}
                              </h4>
                              <p className="text-slate-500">{companyInfo.city}, {companyInfo.country}</p>
                            </div>
                          </div>
                          <button
                            className="px-4 py-2 text-white rounded-lg font-medium"
                            style={{ backgroundColor: companyInfo.primaryColor }}
                          >
                            {t('companySettings.exampleButton') || 'Bouton exemple'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-slate-200 flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={isLoading}
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('companySettings.saving') || 'Sauvegarde...'}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {t('companySettings.saveChanges') || 'Sauvegarder les modifications'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySettings;